/**
 * The panel's Outputs (07 Workshop, rows 12, 37 and 38): documents, ideas and
 * the pictures an agent generated, as one list, newest first; the latest three
 * show, and All N outputs shows the rest, with Show archived at its foot. A document or an idea opens beside the chat and has its own menu
 * (Open, Export, Archive with Undo); a picture opens in its viewer and its
 * menu copies it to the brand library or removes it (pictures have no
 * archive, so removing asks first). Uploaded pictures are Context.
 *
 * Honesty: a kind tag shows only when the data carries a type (documents are
 * untagged until creative-os-backend#114). A list that failed to load keeps
 * what did load, says what did not and offers Try again; its filter count is
 * left out, never zero.
 */
import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { FileText } from "lucide-react";
import { Image } from "@/components/Image";
import { KitInput } from "@/app/routes/dashboard/components/kit/Input";
import { getAiRequestErrorMessage } from "@/lib/api";
import { queue } from "@/components/Toast";
import type {
  WorkshopArtifactListItem,
  WorkshopIdeaListItem,
} from "@/types/workshop.type";
import {
  useWorkshopArtifactMutations,
  useWorkshopArtifacts,
  workshopArtifactQuery,
} from "../api/useWorkshopArtifacts";
import { useWorkshopIdeas } from "../api/useWorkshopIdeas";
import { useWorkshopMedia, type WorkshopMediaItem } from "../api/useWorkshopMedia";
import { formatAttribution, useViewerId } from "../attribution";
import {
  isOutputPicture,
  outputFromArtifact,
  outputFromIdea,
  outputFromMedia,
  outputMeta,
  sortNewestFirst,
  type WorkshopOutput,
} from "../workshopOutputs";
import SourcePreview from "./SourcePreview";
import { KindTag, StateTag, WsInlineError, WsStateBlock } from "./WorkshopParts";
import WorkshopDialog from "./WorkshopDialog";
import { DocMark } from "./WorkshopSearchDialog";
import WorkshopOutputMenu from "./WorkshopOutputMenu";
import WorkshopRowMenu, { type RowMenuItem } from "./WorkshopRowMenu";
import { useOutputActions } from "./useOutputActions";
import { usePictureActions } from "./usePictureActions";

/** The panel shows the latest few; All N outputs shows the rest. */
const FIRST_FEW = 3;

export type RoomOutput = WorkshopOutput & {
  archived: boolean;
  /** Who made it and where, when the record says. */
  made: string | null;
  /** Pictures only: the record, for the viewer and the menu. */
  picture?: WorkshopMediaItem;
};

type Props = {
  brandId: string;
  projectId: string;
  canWrite: boolean;
  /** The pane is a sheet: no "beside", no full width, actions always showing. */
  asSheet: boolean;
  /** Phone: an output's menu is a sheet. */
  isPhone: boolean;
  onOpen: (output: RoomOutput, full?: boolean) => void;
  /** A generated picture to show (from a chat card), until handled. */
  requestedPictureId?: string | null;
  onRequestHandled?: () => void;
  /** A picture was looked at (its glow goes). */
  onNoticePicture?: (mediaId: string) => void;
};

function toRows(
  projectId: string,
  artifacts: WorkshopArtifactListItem[] | undefined,
  ideas: WorkshopIdeaListItem[] | undefined,
  pictures: WorkshopMediaItem[],
  viewerId: string | null,
): RoomOutput[] {
  const rows: RoomOutput[] = [];
  for (const a of artifacts ?? []) {
    const made = formatAttribution(a.attribution?.createdBy, viewerId);
    rows.push({
      ...outputFromArtifact(projectId, a),
      archived: a.status === "archived",
      made: made ? `Made ${made}` : null,
    });
  }
  for (const i of ideas ?? []) {
    const made = formatAttribution(i.attribution?.createdBy, viewerId);
    rows.push({
      ...outputFromIdea(projectId, i),
      archived: i.status === "ARCHIVED",
      made: made ? `Saved ${made}` : null,
    });
  }
  for (const m of pictures) {
    const by = m.attribution?.createdBy;
    const thread = by?.thread;
    const made =
      thread?.state === "own" && thread.title
        ? `Generated in ${thread.title}`
        : formatAttribution(by, viewerId, { includeContext: false });
    rows.push({
      ...outputFromMedia(projectId, m),
      archived: false,
      made: made ? (made.startsWith("Generated") ? made : `Generated ${made}`) : null,
      picture: m,
    });
  }
  return sortNewestFirst(rows);
}

export default function WorkshopRoomOutputs({
  brandId,
  projectId,
  canWrite,
  asSheet,
  isPhone,
  onOpen,
  requestedPictureId = null,
  onRequestHandled,
  onNoticePicture,
}: Props) {
  const [all, setAll] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [renaming, setRenaming] = useState<RoomOutput | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const qc = useQueryClient();
  const { updateArtifact } = useWorkshopArtifactMutations(projectId);
  const [viewing, setViewing] = useState<WorkshopMediaItem | null>(null);
  const viewerId = useViewerId();
  const artifacts = useWorkshopArtifacts(projectId, showArchived);
  const ideas = useWorkshopIdeas(projectId, showArchived);
  const media = useWorkshopMedia(projectId);
  const actions = useOutputActions(projectId, brandId);
  const pictureActions = usePictureActions({
    brandId,
    projectId,
    canWrite,
    onRemoved: (id) => {
      if (viewing?.id === id) setViewing(null);
    },
  });

  const docsFailed = artifacts.isError && artifacts.data === undefined;
  const ideasFailed = ideas.isError && ideas.data === undefined;
  const picturesFailed = media.isError && media.data === undefined;
  const loading = artifacts.isLoading || ideas.isLoading || media.isLoading;

  const generated = useMemo(() => (media.data ?? []).filter(isOutputPicture), [media.data]);
  const rows = useMemo(
    () => toRows(projectId, artifacts.data, ideas.data, generated, viewerId),
    [projectId, artifacts.data, ideas.data, generated, viewerId],
  );
  const listed = showArchived ? rows : rows.filter((row) => !row.archived);
  const shown = all ? listed : listed.slice(0, FIRST_FEW);

  /** A document's new title is saved as its next version (the API keeps both together). */
  const rename = async (row: RoomOutput, title: string) => {
    try {
      const doc = await qc.fetchQuery({ ...workshopArtifactQuery(row.id), staleTime: 0 });
      await updateArtifact.mutateAsync({
        artifactId: row.id,
        content: doc.content,
        title,
        note: "Renamed",
        baseVersion: doc.version,
      });
    } catch (error) {
      queue.add({ title: "Couldn't rename it", description: getAiRequestErrorMessage(error), variant: "error" });
    }
  };

  // A chat card asked for a picture: it shows by being read here, and closing
  // the viewer hands the request back.
  const requested = requestedPictureId
    ? generated.find(
        (m) => m.id === requestedPictureId || m.copiedFromBrandMediaId === requestedPictureId,
      )
    : undefined;
  const shownPicture = viewing ?? requested ?? null;

  const failedParts = [
    docsFailed ? "documents" : null,
    ideasFailed ? "ideas" : null,
    picturesFailed ? "pictures" : null,
  ].filter((part): part is string => part !== null);

  return (
    <div className="ws-room-outputs">
      {failedParts.length > 0 ? (
        <WsInlineError
          className="ws-inline-error--compact"
          title={
            failedParts.length === 3
              ? "Couldn't load this project's outputs"
              : "The rest of the outputs did not load"
          }
          line={
            failedParts.length === 3
              ? "Your connection dropped or the server did not answer. Nothing was lost."
              : "The connection dropped. Nothing was lost."
          }
          onRetry={() => {
            if (docsFailed) void artifacts.refetch();
            if (ideasFailed) void ideas.refetch();
            if (picturesFailed) void media.refetch();
          }}
        />
      ) : null}

      {loading ? (
        <ul className="ws-oitem-list" aria-busy="true" aria-label="Loading outputs">
          {[0, 1, 2].map((i) => (
            <li key={i} className="ws-orow ws-orow--skel" aria-hidden="true">
              <span className="ws-orow-thumb" />
              <span className="ws-orow-text">
                <span className="ws-skel ws-skel--tag" />
                <span className="ws-skel ws-skel--title" />
              </span>
            </li>
          ))}
        </ul>
      ) : shown.length === 0 && failedParts.length === 0 ? (
        <WsStateBlock
          icon={FileText}
          className="ws-state--pane"
          title="No outputs yet"
          line={
            showArchived
              ? "Nothing made in this project, archived or not."
              : "What the chat or an agent makes in this project lands here, newest first."
          }
        />
      ) : (
        <ul className="ws-oitem-list" aria-label="Outputs">
          {shown.map((row) =>
            row.picture ? (
              <PictureOutputRow
                key={row.key}
                row={row}
                picture={row.picture}
                asSheet={asSheet}
                isPhone={isPhone}
                items={pictureActions.menuItems(row.picture)}
                onView={() => {
                  onNoticePicture?.(row.id);
                  setViewing(row.picture ?? null);
                }}
                onAction={(id) => row.picture && pictureActions.run(id, row.picture)}
              />
            ) : (
            <OutputRow
              key={row.key}
              row={row}
              asSheet={asSheet}
              isPhone={isPhone}
              canWrite={canWrite}
              onOpen={onOpen}
              onExport={
                row.kind === "document"
                  ? (format) => void actions.exportDocument(row.id, format)
                  : undefined
              }
              onArchive={
                canWrite
                  ? (archived) => (archived ? actions.archive(row) : actions.restore(row))
                  : undefined
              }
              onRename={
                canWrite && row.kind === "document"
                  ? () => {
                      setRenameValue(row.title);
                      setRenaming(row);
                    }
                  : undefined
              }
            />
            ),
          )}
        </ul>
      )}

      {!loading && listed.length > FIRST_FEW ? (
        <button type="button" className="ws-panel-link" aria-expanded={all} onClick={() => setAll((v) => !v)}>
          {all ? "Fewer" : `All ${listed.length} outputs`}
        </button>
      ) : null}
      {!loading && (all || listed.length <= FIRST_FEW) ? (
        <button type="button" className="ws-panel-link ws-panel-link--quiet" onClick={() => setShowArchived((v) => !v)}>
          {showArchived ? "Hide archived" : "Show archived"}
        </button>
      ) : null}

      <WorkshopDialog
        isOpen={renaming !== null}
        onOpenChange={(open) => {
          if (!open) setRenaming(null);
        }}
        title="Rename"
        description="The new title is saved as the next version."
        cancelLabel="Cancel"
        confirmLabel="Rename"
        confirmDisabled={!renameValue.trim() || renameValue.trim() === renaming?.title}
        onSubmit={() => {
          const row = renaming;
          const title = renameValue.trim();
          setRenaming(null);
          if (row && title) void rename(row, title);
        }}
        form
      >
        <KitInput label="Title" value={renameValue} onChange={(e) => setRenameValue(e.target.value)} maxLength={200} autoFocus />
      </WorkshopDialog>

      <SourcePreview
        brandId={brandId}
        projectId={projectId}
        target={shownPicture ? { kind: "picture", item: shownPicture } : null}
        onClose={() => {
          setViewing(null);
          if (requested) onRequestHandled?.();
        }}
      />
      {pictureActions.elements}
    </div>
  );
}

/** A generated picture: its thumbnail, IMAGE, when, and its own menu. */
function PictureOutputRow({
  row,
  picture,
  asSheet,
  isPhone,
  items,
  onView,
  onAction,
}: {
  row: RoomOutput;
  picture: WorkshopMediaItem;
  asSheet: boolean;
  isPhone: boolean;
  items: RowMenuItem[];
  onView: () => void;
  onAction: (id: string) => void;
}) {
  const meta = outputMeta(row);
  const src = picture.thumbUrl ?? picture.url;
  return (
    <li className="ws-oitem" data-touch={asSheet || undefined}>
      <button
        type="button"
        className="ws-oitem-open"
        onClick={onView}
        aria-label={`View ${row.title}`}
      />
      <span className="ws-oitem-thumb" aria-hidden="true">
        {src ? <Image src={src} alt="" className="ws-oitem-img" /> : <DocMark />}
      </span>
      <span className="ws-oitem-text">
        <span className="ws-oitem-head">
          <span className="ws-oitem-tags">
            {row.tag ? <KindTag>{row.tag}</KindTag> : null}
            {picture.library ? <StateTag>In the library</StateTag> : null}
            {meta ? <span className="ws-oitem-meta">{meta}</span> : null}
          </span>
          <span className="ws-oitem-actions">
            <WorkshopRowMenu
              label={`Actions for ${row.title}`}
              title={row.title}
              meta={meta}
              items={items}
              asSheet={isPhone}
              onAction={(id) => (id === "open" ? onView() : onAction(id))}
            />
          </span>
        </span>
        <span className="ws-oitem-title">{row.title}</span>
      </span>
    </li>
  );
}

function OutputRow({
  row,
  asSheet,
  isPhone,
  canWrite,
  onOpen,
  onExport,
  onArchive,
  onRename,
}: {
  row: RoomOutput;
  asSheet: boolean;
  isPhone: boolean;
  canWrite: boolean;
  onOpen: (output: RoomOutput, full?: boolean) => void;
  onExport?: (format: "md" | "docx" | "pdf") => void;
  onArchive?: (archived: boolean) => void;
  onRename?: () => void;
}) {
  const meta = outputMeta(row);
  const openLabel = asSheet ? `Open ${row.title}` : `Open ${row.title} beside the chat`;
  return (
    <li
      className="ws-oitem"
      data-archived={row.archived || undefined}
      data-touch={asSheet || undefined}
    >
      {/* The whole row opens it beside the chat. */}
      <button
        type="button"
        className="ws-oitem-open"
        onClick={() => onOpen(row)}
        aria-label={openLabel}
      />
      <span className="ws-oitem-thumb" aria-hidden="true">
        <DocMark />
      </span>
      <span className="ws-oitem-text">
        <span className="ws-oitem-head">
          <span className="ws-oitem-tags">
            {row.tag ? <KindTag>{row.tag}</KindTag> : null}
            {row.archived ? <StateTag>Archived</StateTag> : null}
            {meta ? <span className="ws-oitem-meta">{meta}</span> : null}
          </span>
          <span className="ws-oitem-actions">
            <WorkshopOutputMenu
              title={row.title}
              tag={row.tag}
              meta={meta}
              archived={row.archived}
              asSheet={isPhone}
              onOpen={() => onOpen(row)}
              onOpenFull={asSheet ? undefined : () => onOpen(row, true)}
              onExport={onExport}
              onArchive={canWrite ? onArchive : undefined}
              onRename={onRename}
            />
          </span>
        </span>
        <span className="ws-oitem-title">{row.title}</span>
      </span>
    </li>
  );
}
